import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { authService } from './shared/services/auth/auth.service';
import { LocalStorageService } from './shared/services/local-storage/localstorage.service';
import { SocketService } from './shared/services/socket/socket.service';
import { UserPreferencesService } from './shared/services/user-preferences/user-preferences.service';
import { CallerModuleService } from './core/services/caller-module.service';
import { PermissionService } from './core/services/permission.service';
import { GlobalExportProgressService } from './shared/services/export/global-export-progress.service';

const SPLASH_TIMEOUT_MS = 8000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly authSvc = inject(authService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly socketService = inject(SocketService);
  private readonly userPreferencesService = inject(UserPreferencesService);
  private readonly router = inject(Router);
  private readonly callerModuleService = inject(CallerModuleService);
  private readonly permissionService = inject(PermissionService);
  private readonly exportService = inject(GlobalExportProgressService);

  title = 'Colobbo-Web';
  isNavigating = true;
  private initialNavigationDone = false;

  private splashTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private hideSplash(): void {
    this.isNavigating = false;
    if (this.splashTimeoutId !== null) {
      clearTimeout(this.splashTimeoutId);
      this.splashTimeoutId = null;
    }
  }

  ngOnInit(): void {
    // Hard deadline: splash never stays visible beyond SPLASH_TIMEOUT_MS
    this.splashTimeoutId = setTimeout(() => this.hideSplash(), SPLASH_TIMEOUT_MS);


    // Company context is now initialized by APP_INITIALIZER before app loads
    setTimeout(() => {
      const accessToken = this.authSvc.getCurrentAccessToken();
      const userPreferences = this.localStorageService.getItem<any>('userPreferences');
      if (!accessToken || this.authSvc.isTokenExpired(accessToken)) return;
      if (!userPreferences?.selectedCompanyId || !userPreferences?.selectedProjectId) return;
      this.socketService.initialize();
    }, 0);

    this.permissionService.loaded$.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.exportService.init();
    });

    // Keep X-Caller-Module header in sync with the active route
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Clear stale module name so the old value doesn't bleed into the new route
        this.callerModuleService.clearCaller();
      } else if (event instanceof NavigationEnd) {
        if (!this.initialNavigationDone) {
          this.initialNavigationDone = true;
          this.hideSplash();
        }
        const routePath = event.urlAfterRedirects.split('?')[0];
        const moduleName = this.permissionService.getModuleNameForRoute(routePath);
        if (moduleName) {
          this.callerModuleService.setCaller(moduleName);
        }
      } else if (event instanceof NavigationError || event instanceof NavigationCancel) {
        if (!this.initialNavigationDone) {
          this.initialNavigationDone = true;
          this.hideSplash();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.splashTimeoutId !== null) {
      clearTimeout(this.splashTimeoutId);
    }
  }
}
